import {
  CheckCircleFilled,
  LoadingOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import BasicButton from "@components/core/buttons/BasicButton";
import DeleteIcon from "@static/core/icons/icon-delete.svg?react";
import { Image, ImageRules } from "@type/core/image/image.types";
import { IMG_DEFAULT_ALT } from "@utils/Constant";
import { getBase64, getBase64UrlFrom } from "@utils/helpers/base64-helper";
import { beforeUpload } from "@utils/helpers/image-helper";
import { Upload } from "antd";
import { UploadChangeParam } from "antd/lib/upload/interface";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

interface UploadImageFieldProps {
  field: string;
  editMode?: boolean;
  imageContent: Image | undefined;
  newsId?: string;
  inputIndex?: number;
  onChangeCallBack?: (
    imageContent: Image,
    index?: number,
    inputId?: string,
  ) => void;
  setImage?: (image: Image | undefined) => void;
  rules?: ImageRules;
  fieldPrefix?: string;
  inputId?: string;
  className?: string;
  showInfos?: boolean;
  showMetadata?: boolean;
  imageContentLoading?: boolean;
  isSelected?: boolean;
  isDefaultImage?: boolean;
  isSignatureImage?: boolean;
  sizeErrorI18nKey?: string;
}

const UploadImageField: FunctionComponent<UploadImageFieldProps> = ({
  field,
  inputIndex,
  onChangeCallBack,
  imageContent,
  editMode = true,
  rules,
  setImage,
  inputId,
  className = "",
  imageContentLoading = false,
  isSelected = false,
  isDefaultImage = false,
  isSignatureImage,
  sizeErrorI18nKey,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);
  const handleImageChange = (info: UploadChangeParam) => {
    setLoading(true);
    if (
      info.file.originFileObj &&
      info.file.percent === 100 &&
      info.file.status === "done"
    ) {
      getBase64(info.file.originFileObj, (imageBase64Data) => {
        handleChange(
          info.file.originFileObj as Blob,
          imageBase64Data as string,
          info.file.type ?? "",
          info.file.name,
        );
      });
    }
  };

  const encodedImageContent =
    imageContent && !imageContent?.base64Data?.includes("base64")
      ? getBase64UrlFrom(imageContent.base64Data, imageContent.type)
      : imageContent?.base64Data;

  const uploadButton = (
    <div className="ant-upload-button">
      {loading || imageContentLoading ? (
        <LoadingOutlined />
      ) : (
        <UploadOutlined />
      )}
      <div className="ant-upload-text">Importer</div>
    </div>
  );

  const handleChange = (
    file: Blob,
    content: string,
    type: string,
    name: string,
  ) => {
    if (onChangeCallBack && inputIndex !== undefined) {
      onChangeCallBack(
        {
          file: file,
          base64Data: content,
          type: type,
          name: name,
        },
        inputIndex,
        inputId,
      );
    }
    if (setImage) {
      setImage({
        file: file,
        base64Data: content,
        type: type,
        name: name,
      });
    }
    setLoading(false);
  };

  const handleDelete = () => {
    setImage && setImage(undefined);
  };

  return (
    <div
      className={`${className ? className : ""} upload-file ${
        isSelected ? "valid" : ""
      }`}
    >
      <div
        className={`d-flex flex-column flex-sm-row align-items-center mr-10 ${
          imageContent ? "" : "justify-content-center"
        }`}
      >
        <Upload
          name="image"
          className={isSignatureImage ? "signature-upload" : ""}
          listType={
            encodedImageContent && isSignatureImage ? "picture" : "picture-card"
          }
          showUploadList={false}
          beforeUpload={(file) => {
            return beforeUpload(file, rules, sizeErrorI18nKey);
          }}
          onChange={handleImageChange}
          disabled={!editMode}
          data-test-type="input-file"
          data-test={field}
        >
          {encodedImageContent ? (
            <img
              src={encodedImageContent}
              alt={IMG_DEFAULT_ALT}
              className="mw-100 mh-100"
            />
          ) : (
            uploadButton
          )}
        </Upload>
        {!isDefaultImage && encodedImageContent && (
          <div className="d-flex align-items-center">
            <>
              <BasicButton
                disabled={!editMode}
                text={
                  <DeleteIcon
                    title={t("account.myInformations.deleteImage")}
                    className="cursor-pointer icon-30"
                  />
                }
                variant="white ml-10"
                onClick={handleDelete}
              />
            </>
          </div>
        )}
      </div>
      {isSelected && <CheckCircleFilled className="check-sign" />}
    </div>
  );
  // {/* </Form.Item> */}
};

export default UploadImageField;
